import type { Platform } from '#shared/schemas/device';

/**
 * Which install instructions to show, and the `platform` field the join call sends.
 * Pure on a user agent string so it can be tested.
 */
export type GatePlatform =
  | 'ios-safari'
  | 'ios-other'
  | 'android-chrome'
  | 'android-samsung'
  | 'android-other'
  | 'desktop-chromium'
  | 'desktop-safari'
  | 'desktop-firefox'
  | 'desktop-other';

export interface PlatformInfo {
  gate: GatePlatform;
  platform: Platform;
  /** Instagram, Messenger, TikTok and friends: they cannot install anything. */
  inAppBrowser: boolean;
}

const IN_APP = /Instagram|FBAN|FBAV|FB_IAB|Messenger|Line\/|Snapchat|musical_ly|BytedanceWebview|TikTok/i;

export function detectPlatform(ua: string, maxTouchPoints = 0): PlatformInfo {
  const inAppBrowser = IN_APP.test(ua);
  // iPadOS reports itself as a Mac with touch.
  const isIOS = /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && maxTouchPoints > 1);
  if (isIOS) {
    const otherBrowser = /CriOS|FxiOS|EdgiOS|OPiOS|GSA\//i.test(ua);
    return { gate: otherBrowser || inAppBrowser ? 'ios-other' : 'ios-safari', platform: 'ios', inAppBrowser };
  }
  if (/Android/i.test(ua)) {
    if (/SamsungBrowser/i.test(ua)) return { gate: 'android-samsung', platform: 'android', inAppBrowser };
    if (/Chrome\//i.test(ua) && !/EdgA|OPR|Firefox/i.test(ua) && !inAppBrowser)
      return { gate: 'android-chrome', platform: 'android', inAppBrowser };
    return { gate: 'android-other', platform: 'android', inAppBrowser };
  }
  if (/Firefox\//i.test(ua)) return { gate: 'desktop-firefox', platform: 'desktop', inAppBrowser };
  if (/Chrome\/|Chromium|Edg\//i.test(ua)) return { gate: 'desktop-chromium', platform: 'desktop', inAppBrowser };
  if (/Safari\//i.test(ua) && /Macintosh/i.test(ua))
    return { gate: 'desktop-safari', platform: 'desktop', inAppBrowser };
  return { gate: 'desktop-other', platform: /Windows|Macintosh|Linux/i.test(ua) ? 'desktop' : 'other', inAppBrowser };
}

let cached: PlatformInfo | null = null;
export function platformInfo(): PlatformInfo {
  if (!cached) cached = detectPlatform(navigator.userAgent, navigator.maxTouchPoints ?? 0);
  return cached;
}

export const isAndroid = (): boolean => platformInfo().platform === 'android';
export const isIOS = (): boolean => platformInfo().platform === 'ios';

/** A short name for the Devices list ("iPhone", "Android phone"). Sent when a phone signs in. */
export function deviceLabel(ua: string, maxTouchPoints = 0): string {
  const info = detectPlatform(ua, maxTouchPoints);
  if (info.platform === 'ios')
    return /iPad/i.test(ua) || (/Macintosh/i.test(ua) && maxTouchPoints > 1) ? 'iPad' : 'iPhone';
  if (info.platform === 'android') return 'Android phone';
  if (info.platform === 'desktop') return 'Computer';
  return 'Phone';
}
